import { formatAiKbTitle } from "@/lib/kb/formatKbArticleTitle";
import { adminPost } from "@/lib/kbAdminClient";
import type {
  AiHelpResponse,
  AiQuestionAnswer,
  AppliedSuggestion,
  KbAudience,
  KbScope,
  KbStatus,
  PanelNote,
} from "./types";

export type RunKbAiHelpMode = "title_only" | "metadata_only" | "full" | "stop_and_draft";

export type RunKbAiHelpContext = {
  mode: RunKbAiHelpMode;
  articleContent: string;
  aiPurpose: string;
  aiAudienceHint: "" | "customer" | "internal" | "both" | "any";
  aiScopeHint: "" | "app" | "manufacturer" | "org";
  aiDocTypeHint: string;
  aiSourceKind: "notes" | "body" | "policy" | "process" | "product" | "training";
  articleSlug: string;
  articleTitle: string;
  defaultArticleTitle: string;
  articleDocType: string;
  articleScope: KbScope;
  articleTagsInput: string;
  aiQuestionAnswers: AiQuestionAnswer[];
  aiResolvedFacts: string[];
  preserveOriginalBody: boolean;
  aiUpdateTitle: boolean;
  aiUpdateMetadata: boolean;
  canManageManufacturerScope: boolean;
  canManageAppScope: boolean;
  canPublishKb: boolean;
  /** Org branding for AI titles (e.g. Bennett Buildings). */
  organizationDisplayName: string;
  mergeAiQuestions: (questions: string[]) => void;
  showNote: (next: PanelNote | null) => void;
  showAiFeedback: (next: PanelNote | null) => void;
  setAiBusy: (busy: boolean) => void;
  setArticleTitle: (value: string | ((prev: string) => string)) => void;
  setArticleAudience: (value: KbAudience | ((prev: KbAudience) => KbAudience)) => void;
  setArticleScope: (value: KbScope | ((prev: KbScope) => KbScope)) => void;
  setArticleDocType: (value: string | ((prev: string) => string)) => void;
  setArticleStatus: (value: KbStatus | ((prev: KbStatus) => KbStatus)) => void;
  setArticleTagsInput: (value: string | ((prev: string) => string)) => void;
  setAiGeneratedNotes: (value: string[]) => void;
  setAiResolvedFacts: (value: string[]) => void;
  setAiPlacementSummary: (value: string) => void;
  setAiSuggestedAction: (value: AppliedSuggestion["suggested_action"] | "") => void;
  setAiTopicComplexity: (value: AppliedSuggestion["topic_complexity"] | "") => void;
  setAiRecommendedDocCount: (value: number) => void;
  setAiStopReason: (value: AppliedSuggestion["stop_reason"] | "") => void;
  setAiFollowOnDocs: (value: AppliedSuggestion["follow_on_docs"]) => void;
  setAiKbGapCandidates: (value: AppliedSuggestion["kb_gap_candidates"]) => void;
  setAiArticleLinkSuggestions: (value: AppliedSuggestion["article_link_suggestions"]) => void;
  setAiRelatedArticles: (value: AppliedSuggestion["related_articles"]) => void;
  setAiBodyDraft: (value: string | ((prev: string) => string)) => void;
  setAiBodyGenerationNotes: (value: string[]) => void;
  setAiLinksOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  setAppliedSuggestion: (value: AppliedSuggestion | null) => void;
};

export async function runKbAiHelpFlow(ctx: RunKbAiHelpContext): Promise<void> {
  const {
    mode,
    articleContent,
    aiPurpose,
    aiAudienceHint,
    aiScopeHint,
    aiDocTypeHint,
    aiSourceKind,
    articleSlug,
    articleTitle,
    defaultArticleTitle,
    articleDocType,
    articleScope,
    articleTagsInput,
    aiQuestionAnswers,
    aiResolvedFacts,
    preserveOriginalBody,
    aiUpdateTitle,
    aiUpdateMetadata,
    canManageManufacturerScope,
    canManageAppScope,
    canPublishKb,
    organizationDisplayName,
    mergeAiQuestions,
    showNote,
    showAiFeedback,
    setAiBusy,
    setArticleTitle,
    setArticleAudience,
    setArticleScope,
    setArticleDocType,
    setArticleStatus,
    setArticleTagsInput,
    setAiGeneratedNotes,
    setAiResolvedFacts,
    setAiPlacementSummary,
    setAiSuggestedAction,
    setAiTopicComplexity,
    setAiRecommendedDocCount,
    setAiStopReason,
    setAiFollowOnDocs,
    setAiKbGapCandidates,
    setAiArticleLinkSuggestions,
    setAiRelatedArticles,
    setAiBodyDraft,
    setAiBodyGenerationNotes,
    setAiLinksOpen,
    setAppliedSuggestion,
  } = ctx;

  showNote(null);
  showAiFeedback(null);
  if (!articleContent.trim() && !aiPurpose.trim()) {
    const next = { tone: "error", title: "AI processing needs context", text: "Add a document purpose, body text, or both before generating suggestions." } as const;
    showAiFeedback(next);
    showNote(next);
    return;
  }

  setAiBusy(true);
  try {
    const response = await adminPost<AiHelpResponse>("/api/kb/admin/assist", {
      purpose: aiPurpose,
      content: articleContent,
      audienceHint: aiAudienceHint || undefined,
      scopeHint: aiScopeHint || undefined,
      docTypeHint: aiDocTypeHint || undefined,
      sourceKind: aiSourceKind,
      organizationDisplayName: organizationDisplayName.trim() || undefined,
      currentSlug: articleSlug || undefined,
      currentTitle: articleTitle && articleTitle !== defaultArticleTitle ? articleTitle : undefined,
      currentDocType: articleDocType || undefined,
      currentScope: articleScope || undefined,
      currentTags: articleTagsInput.split(",").map((tag) => tag.trim()).filter(Boolean),
      answeredQuestions: aiQuestionAnswers.filter((item) => item.disposition === "answered" && item.answer.trim()).map((item) => ({ question: item.question, answer: item.answer })),
      invalidatedQuestions: aiQuestionAnswers
        .filter((item) => item.disposition === "not_applicable" || item.disposition === "different_document")
        .map((item) => ({ question: item.question, disposition: item.disposition, note: item.answer.trim() })),
      resolvedFacts: aiResolvedFacts,
      bodyGenerationAction: mode === "stop_and_draft" ? "draft_body" : undefined,
      stopAsking: mode === "stop_and_draft",
      preserveBody: mode === "stop_and_draft" ? preserveOriginalBody : false,
    });

    if (!response.ok) {
      const next = { tone: "error", title: "AI processing failed", text: response.error } as const;
      showAiFeedback(next);
      showNote(next);
      return;
    }

    const suggestion = response.data.suggestion;
    const applyTitle = mode === "title_only" || (mode !== "metadata_only" && aiUpdateTitle);
    const applyMetadata = mode === "metadata_only" || (mode !== "title_only" && aiUpdateMetadata);
    if (applyTitle) {
      const nextRaw = suggestion.title || articleTitle || defaultArticleTitle;
      setArticleTitle(formatAiKbTitle(nextRaw, organizationDisplayName));
    }
    if (applyMetadata) {
      setArticleAudience(suggestion.audience);
      const requestedScope = suggestion.scope;
      const normalizedScope: KbScope =
        requestedScope === "app"
          ? (canManageAppScope ? "app" : canManageManufacturerScope ? "manufacturer" : "org")
          : requestedScope === "manufacturer"
            ? (canManageManufacturerScope ? "manufacturer" : "org")
            : "org";
      setArticleScope(normalizedScope);
      setArticleDocType(suggestion.doc_type || "");
      setArticleStatus(suggestion.status === "published" && canPublishKb ? "published" : "draft");
      setArticleTagsInput((suggestion.tags || []).join(", "));
    }
    setAiGeneratedNotes(suggestion.notes || []);
    setAiResolvedFacts(suggestion.resolved_facts || []);
    setAiPlacementSummary(suggestion.placement_summary || "");
    setAiSuggestedAction(suggestion.suggested_action || "");
    setAiTopicComplexity(suggestion.topic_complexity || "");
    setAiRecommendedDocCount(Number(suggestion.recommended_doc_count || 1) || 1);
    setAiStopReason(suggestion.stop_reason || "");
    setAiFollowOnDocs(suggestion.follow_on_docs || []);
    setAiKbGapCandidates(suggestion.kb_gap_candidates || []);
    setAiArticleLinkSuggestions(suggestion.article_link_suggestions || []);
    setAiRelatedArticles(suggestion.related_articles || []);
    setAiBodyDraft(suggestion.body_draft || "");
    setAiBodyGenerationNotes(suggestion.body_generation_notes || []);
    setAiLinksOpen((suggestion.article_link_suggestions || []).length > 0);
    mergeAiQuestions(suggestion.follow_up_questions || []);
    setAppliedSuggestion({
      ...suggestion,
      accessSummary: `Allowed scopes: ${response.data.access.allowedScopes.join(", ")} • ${response.data.access.canPublish ? "publish allowed" : "draft only"}`,
    });

    const next = {
      tone: "success",
      title: "AI processing applied",
      text:
        mode === "stop_and_draft"
          ? preserveOriginalBody
            ? "AI generated a draft with your original body preserved. Question answers were incorporated as additions. Review the draft and append or restore as needed."
            : "AI stopped the interview and produced a draft body. Review it below, then append or replace the article body."
          : mode === "title_only"
            ? "Title was generated from your content."
            : mode === "metadata_only"
              ? "Permissions and metadata were updated from your content."
              : suggestion.follow_up_questions?.length
                ? "Metadata was updated and new clarifying questions were staged below. Answer them and run AI processing again to refine placement guidance."
                : "Metadata was updated and the current interview memory was applied. Review the placement guidance and related article suggestions before saving.",
    } as const;
    showAiFeedback(next);
    showNote(next);
  } catch (error) {
    const next = { tone: "error", title: "AI processing failed", text: error instanceof Error ? error.message : String(error) } as const;
    showAiFeedback(next);
    showNote(next);
  } finally {
    setAiBusy(false);
  }
}
